import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import {
	Box,
	IconButton,
	Paper,
	TableContainer,
	TableBody,
	TableCell,
	TableHead,
	TableRow,
	Table,
	Typography,
	Stack,
	TextField,
	InputAdornment,
	Button
} from '@mui/material';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import PersonAddRoundedIcon from '@mui/icons-material/PersonAddRounded';
import VisibilityRoundedIcon from '@mui/icons-material/VisibilityRounded';
import DeleteRoundedIcon from '@mui/icons-material/DeleteRounded';
import { isEmpty, isUndefined } from 'lodash';

import { getRuta, isAccess, simpleMessage } from '../../helpers/Helpers';
import { getToken } from '../../api/Account';
import { DataContext } from '../../context/DataContext';
import { deleteClientAsync, getClientListAsync } from '../../api/Clients';
import NoData from '../../components/NoData';
import { PaginationComponent } from '../../components/PaginationComponent';
import Swal from 'sweetalert2';
import { monoFont } from '../../theme';

const initials = (fullName) => {
	const parts = (fullName || '').trim().split(/\s+/);
	return `${(parts[0] || '')[0] || ''}${(parts[1] || '')[0] || ''}`.toUpperCase();
};

const ClientList = () => {
	const [clientList, setClientList] = useState([]);
	const [localReload, setLocalReload] = useState(false);

	const { setIsLoading, setIsUnautorized, access, selectedSucursal } = useContext(DataContext);

	let ruta = getRuta();
	const token = getToken();
	let navigate = useNavigate();

	const [searchTerm, setSearchTerm] = useState('');

	const withSearch = clientList.filter((val) => {
		if (searchTerm === '') {
			return val;
		} else if (
			val.id.toString().includes(searchTerm) ||
			val.fullName.toString().includes(searchTerm) ||
			val.identification.toString().includes(searchTerm)
		) {
			return val;
		}
		return false;
	});

	useEffect(() => {
		if (selectedSucursal === 0 || isUndefined(selectedSucursal)) {
			return;
		}
		getClients(selectedSucursal);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [selectedSucursal, localReload]);

	const [currentPage, setCurrentPage] = useState(1);
	const [itemsperPage] = useState(10);
	const indexLast = currentPage * itemsperPage;
	const indexFirst = indexLast - itemsperPage;
	const currentItem = withSearch.slice(indexFirst, indexLast);
	const paginate = (pageNumber) => setCurrentPage(pageNumber);

	const onChangeSearch = (val) => {
		setCurrentPage(1);
		setSearchTerm(val);
		paginate(1);
	};

	const deleteClient = async (item) => {
		Swal.fire({
			title: '¿Estas Seguro?',
			text: 'No podras revertir esto...!',
			icon: 'warning',
			showCancelButton: true,

			confirmButtonColor: '#2979ff',
			cancelButtonColor: '#f50057',

			confirmButtonText: 'Eliminar!',
			cancelButtonText: 'Cancelar',
			customClass: {
				popup: 'border'
			}
		}).then((result) => {
			if (result.isConfirmed) {
				(async () => {
					setIsLoading(true);
					const result = await deleteClientAsync(token, item.id);
					if (!result.statusResponse) {
						setIsLoading(false);
						if (result.error === 'Unauthorized') {
							setIsUnautorized(true);
							return;
						}
						simpleMessage(result.error, 'error');
						return;
					}

					setIsLoading(false);
					setLocalReload(!localReload);

					Swal.fire('Eliminado!', 'Cliente Eliminado.', 'success');
				})();
			}
		});
	};

	const getClients = async (sucursal) => {
		setIsLoading(true);
		const result = await getClientListAsync(token, sucursal);
		if (!result.statusResponse) {
			setIsLoading(false);
			if (result.error === 'Unauthorized') {
				setIsUnautorized(true);
				return;
			}
			simpleMessage(result.error, 'error');
			return;
		}

		setIsLoading(false);
		setClientList(result.data.result);
	};

	return (
		<div>
			<Stack
				direction={{ xs: 'column', sm: 'row' }}
				justifyContent="space-between"
				alignItems={{ xs: 'stretch', sm: 'center' }}
				spacing={1.5}
				flexWrap="wrap"
				sx={{ marginBottom: '18px' }}
			>
				<TextField
					size="small"
					onChange={(e) => onChangeSearch(e.target.value.toUpperCase())}
					value={searchTerm}
					placeholder="Buscar por nombre, cédula o No..."
					sx={{ width: { xs: '100%', sm: 300 } }}
					InputProps={{
						startAdornment: (
							<InputAdornment position="start">
								<SearchRoundedIcon sx={{ fontSize: 19, color: 'text.disabled' }} />
							</InputAdornment>
						)
					}}
				/>
				{isAccess(access, 'CLIENTS CREATE') ? (
					<Button
						onClick={() => navigate(`${ruta}/addclient`)}
						startIcon={<PersonAddRoundedIcon />}
						variant="contained"
						color="primary"
						sx={{ whiteSpace: 'nowrap' }}
					>
						Agregar cliente
					</Button>
				) : null}
			</Stack>

			{isEmpty(currentItem) ? (
				<NoData />
			) : (
				<Paper sx={{ overflow: 'hidden' }}>
					<TableContainer sx={{ overflowX: 'auto' }}>
						<Table size="small" sx={{ minWidth: 860 }}>
							<TableHead>
								<TableRow>
									<TableCell>No.</TableCell>
									<TableCell>Nombre completo</TableCell>
									<TableCell>Cédula</TableCell>
									<TableCell align="center">Teléfono</TableCell>
									<TableCell align="center">Créd. activos</TableCell>
									<TableCell align="center">Ciclos</TableCell>
									{isAccess(access, 'CLIENTS UPDATE') || isAccess(access, 'CLIENTS DELETE') ? (
										<TableCell align="center">Acciones</TableCell>
									) : null}
								</TableRow>
							</TableHead>
							<TableBody>
								{currentItem.map((row) => (
									<TableRow
										key={row.id}
										hover
										onClick={() => navigate(`${ruta}/client/${row.id}`)}
										sx={{ cursor: 'pointer' }}
									>
										<TableCell sx={{ fontFamily: monoFont, color: 'text.disabled' }}>{row.id}</TableCell>
										<TableCell>
											<Stack direction="row" spacing={1.3} alignItems="center">
												<Box
													sx={{
														width: 34,
														height: 34,
														borderRadius: '9px',
														backgroundColor: 'success.light',
														color: 'success.main',
														display: 'flex',
														alignItems: 'center',
														justifyContent: 'center',
														fontSize: 12,
														fontWeight: 600,
														flexShrink: 0
													}}
												>
													{initials(row.fullName)}
												</Box>
												<Typography sx={{ fontSize: 13.5, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
													{row.fullName}
												</Typography>
											</Stack>
										</TableCell>
										<TableCell sx={{ fontFamily: monoFont, fontSize: 12.5 }}>{row.identification}</TableCell>
										<TableCell align="center" sx={{ fontFamily: monoFont, fontSize: 12.5 }}>
											{row.phone}
										</TableCell>
										<TableCell align="center" sx={{ fontFamily: monoFont, fontWeight: 600, color: 'success.main' }}>
											{row.activeCredits}
										</TableCell>
										<TableCell align="center" sx={{ fontFamily: monoFont }}>
											{row.ciclos}
										</TableCell>
										{isAccess(access, 'CLIENTS UPDATE') || isAccess(access, 'CLIENTS DELETE') ? (
											<TableCell align="center" onClick={(e) => e.stopPropagation()}>
												<Stack direction={'row'} spacing={0.5} justifyContent="center">
													{isAccess(access, 'CLIENTS UPDATE') ? (
														<IconButton size="small" onClick={() => navigate(`${ruta}/client/${row.id}`)}>
															<VisibilityRoundedIcon sx={{ fontSize: 18 }} />
														</IconButton>
													) : null}
													{isAccess(access, 'CLIENTS DELETE') ? (
														<IconButton size="small" sx={{ color: 'error.main' }} onClick={() => deleteClient(row)}>
															<DeleteRoundedIcon sx={{ fontSize: 18 }} />
														</IconButton>
													) : null}
												</Stack>
											</TableCell>
										) : null}
									</TableRow>
								))}
							</TableBody>
						</Table>
					</TableContainer>
					<Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 20px', flexWrap: 'wrap', gap: '10px' }}>
						<Typography sx={{ fontSize: 12.5, color: 'text.secondary' }}>
							Mostrando <strong>{currentItem.length}</strong> de {withSearch.length} clientes
						</Typography>
						<PaginationComponent data={withSearch} paginate={paginate} itemsperPage={itemsperPage} />
					</Box>
				</Paper>
			)}
		</div>
	);
};

export default ClientList;
